import { useState, useMemo } from 'react'
import { Card, Tag, Button, Icon, Title, Text, Table, Collapsible } from '@nimbus-ds/components'
import {
  ShoppingCartIcon,
  ExclamationCircleIcon,
  StarIcon,
  StatsIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ArrowsVerticalIcon,
} from '@nimbus-ds/icons'

interface TNXProduct {
  product_id: string
  product_name: string
  views: number
  conversions: number
  conversion_rate: number
  revenue?: number
}

interface GA4Product {
  name?: string
  product_name?: string
  id?: string
  item_id?: string
  views?: number
  add_to_cart?: number
  add_to_carts?: number
  purchases?: number
  revenue?: number
  conversion_rate?: number
}

interface ProductPerformanceTableProps {
  tnxProducts?: TNXProduct[]
  ga4Products?: GA4Product[] | null
}

interface ProductRow {
  id: string
  name: string
  views: number
  addToCart: number | null
  cartRate: number | null  // add_to_cart / views
  purchases: number
  purchaseRate: number     // purchases / views
  revenue: number | null
  rank: number             // 1 = best converter
  percentile: number       // 0-100, higher = better
}

type SortKey = 'views' | 'cartRate' | 'purchaseRate' | 'revenue' | 'rank'
type SortDirection = 'asc' | 'desc'

export function ProductPerformanceTable({
  tnxProducts,
  ga4Products,
}: ProductPerformanceTableProps) {
  const [sortKey, setSortKey] = useState<SortKey>('views')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const [isExpanded, setIsExpanded] = useState(false)

  const { productData, hasGA4Data, totalProducts } = useMemo(() => {
    const ga4List = Array.isArray(ga4Products) ? ga4Products : []
    const hasTNX = !!tnxProducts && tnxProducts.length > 0

    let products: ProductRow[] = []
    // Prefer TNX (window-aware); fall back to GA4 (single-snapshot).
    // hasGA4Data flag governs the add_to_cart column, only available in GA4.
    let usingGA4 = false

    if (hasTNX) {
      products = tnxProducts!
        .filter(p => p.views > 0 && p.product_name !== 'Unknown')
        .map(p => ({
          id: p.product_id,
          name: p.product_name,
          views: p.views,
          addToCart: null,
          cartRate: null,
          purchases: p.conversions,
          purchaseRate: Math.min(p.conversion_rate, 100),
          revenue: p.revenue ?? null,
          rank: 0,
          percentile: 0,
        }))
    } else if (ga4List.length > 0) {
      usingGA4 = true
      products = ga4List
        .filter(p => (p.views || 0) > 0)
        .map((p, idx) => {
          const views = p.views || 0
          const addToCart = p.add_to_cart ?? p.add_to_carts ?? null
          const purchases = p.purchases || 0

          const cartRate = addToCart !== null && views > 0
            ? Math.min((addToCart / views) * 100, 100)
            : null
          const purchaseRate = views > 0
            ? Math.min((purchases / views) * 100, 100)
            : 0

          return {
            id: p.id || p.item_id || `ga4-${idx}`,
            name: p.name || p.product_name || 'Sin nombre',
            views,
            addToCart,
            cartRate,
            purchases,
            purchaseRate,
            revenue: p.revenue ?? null,
            rank: 0,
            percentile: 0,
          }
        })
    }

    // Calculate ranking by purchase rate (only for products with enough views)
    const rankableProducts = products.filter(p => p.views >= 10)
    const sortedByRate = [...rankableProducts].sort((a, b) => b.purchaseRate - a.purchaseRate)

    sortedByRate.forEach((p, idx) => {
      p.rank = idx + 1
      p.percentile = Math.round(((sortedByRate.length - idx) / sortedByRate.length) * 100)
    })

    // Products with < 10 views get no rank
    products.forEach(p => {
      if (p.views < 10) {
        p.rank = 0
        p.percentile = 0
      }
    })

    return { productData: products, hasGA4Data: usingGA4, totalProducts: rankableProducts.length }
  }, [tnxProducts, ga4Products])

  const sortedData = useMemo(() => {
    return [...productData].sort((a, b) => {
      let aVal: number | null = null
      let bVal: number | null = null

      switch (sortKey) {
        case 'views':
          aVal = a.views
          bVal = b.views
          break
        case 'cartRate':
          aVal = a.cartRate
          bVal = b.cartRate
          break
        case 'purchaseRate':
          aVal = a.purchaseRate
          bVal = b.purchaseRate
          break
        case 'revenue':
          aVal = a.revenue
          bVal = b.revenue
          break
        case 'rank':
          // For rank, lower is better, but we want "best" at top when desc
          aVal = a.rank || 999
          bVal = b.rank || 999
          break
      }

      if (aVal === null && bVal === null) return 0
      if (aVal === null) return 1
      if (bVal === null) return -1

      const multiplier = sortDirection === 'desc' ? -1 : 1
      // For rank, reverse the logic (lower rank = better)
      if (sortKey === 'rank') {
        return (aVal - bVal) * -multiplier
      }
      return (aVal - bVal) * multiplier
    })
  }, [productData, sortKey, sortDirection])

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection(prev => prev === 'desc' ? 'asc' : 'desc')
    } else {
      setSortKey(key)
      setSortDirection('desc')
    }
  }

  const baseData = sortedData.slice(0, 10)
  const extraData = sortedData.slice(10)
  const hasMoreItems = sortedData.length > 10

  const SortableHeader = ({ label, sortKeyValue }: { label: string; sortKeyValue: SortKey }) => (
    <Button appearance="transparent" onClick={() => toggleSort(sortKeyValue)}>
      {label}
      <Icon source={<ArrowsVerticalIcon />} color="currentColor" />
    </Button>
  )

  const formatCurrency = (value: number | null) => {
    if (value === null) return '-'
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value)
  }

  const getRankBadge = (rank: number, percentile: number, total: number) => {
    if (rank === 0) return <Text fontSize="caption" as="span">pocas vistas</Text>

    // Top 20% = green, Bottom 20% = red, middle = neutral
    if (percentile >= 80) {
      return (
        <Tag appearance="success">
          <Icon source={<StarIcon />} color="currentColor" />
          #{rank}/{total}
        </Tag>
      )
    } else if (percentile <= 20) {
      return (
        <Tag appearance="danger">
          <Icon source={<StatsIcon />} color="currentColor" />
          #{rank}/{total}
        </Tag>
      )
    }
    return (
      <Text as="span" className="font-mono">
        #{rank}/{total}
      </Text>
    )
  }

  const renderRow = (product: ProductRow) => (
    <Table.Row key={product.id}>
      <Table.Cell>
        <div className="truncate max-w-[200px]" title={product.name}>
          <Text as="span" fontWeight="medium" color="neutral-textHigh">
            {product.name}
          </Text>
        </div>
      </Table.Cell>
      <Table.Cell className="text-right font-mono">
        {product.views.toLocaleString()}
      </Table.Cell>
      {hasGA4Data && (
        <Table.Cell className="text-right font-mono">
          {product.cartRate !== null ? `${product.cartRate.toFixed(1)}%` : '-'}
        </Table.Cell>
      )}
      <Table.Cell className="text-right">
        <span className={`font-mono ${
          product.rank > 0 && product.percentile >= 80 ? 'text-emerald-600 font-semibold' :
          product.rank > 0 && product.percentile <= 20 ? 'text-red-600' : ''
        }`}>
          {product.purchaseRate.toFixed(1)}%
        </span>
      </Table.Cell>
      <Table.Cell className="text-right font-mono">
        {formatCurrency(product.revenue)}
      </Table.Cell>
      <Table.Cell className="text-right">
        {getRankBadge(product.rank, product.percentile, totalProducts)}
      </Table.Cell>
    </Table.Row>
  )

  if (productData.length === 0) {
    return (
      <Card>
        <Card.Header>
          <div className="flex items-center gap-2">
            <Icon source={<ShoppingCartIcon />} />
            <Title as="h3">Rendimiento por Producto</Title>
          </div>
          <Text>Compara tasas de conversion entre productos</Text>
        </Card.Header>
        <Card.Body>
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="mb-3">
              <Icon source={<ExclamationCircleIcon />} />
            </div>
            <Text>No hay datos de productos disponibles.</Text>
          </div>
        </Card.Body>
      </Card>
    )
  }

  return (
    <Card>
      <Card.Header>
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Icon source={<ShoppingCartIcon />} />
              <Title as="h3">Rendimiento por Producto</Title>
            </div>
            <Text>Compara tasas de conversion entre productos</Text>
          </div>
          <div className="shrink-0 whitespace-nowrap">
            <Tag appearance="neutral">{totalProducts} productos con 10+ vistas</Tag>
          </div>
        </div>
      </Card.Header>
      <Card.Body>
        <Table minWidth="760px" stickyScrollbar>
          <Table.Head>
            <Table.Row>
              <Table.Cell as="th" className="w-[220px]">Producto</Table.Cell>
              <Table.Cell as="th" className="text-right">
                <SortableHeader label="Vistas" sortKeyValue="views" />
              </Table.Cell>
              {hasGA4Data && (
                <Table.Cell as="th" className="text-right">
                  <SortableHeader label="% Add Cart" sortKeyValue="cartRate" />
                </Table.Cell>
              )}
              <Table.Cell as="th" className="text-right">
                <SortableHeader label="% Compra" sortKeyValue="purchaseRate" />
              </Table.Cell>
              <Table.Cell as="th" className="text-right">
                <SortableHeader label="Revenue" sortKeyValue="revenue" />
              </Table.Cell>
              <Table.Cell as="th" className="text-right">
                <SortableHeader label="Ranking" sortKeyValue="rank" />
              </Table.Cell>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {baseData.map(renderRow)}
          </Table.Body>
        </Table>

        {hasMoreItems && (
          <>
            <Collapsible open={isExpanded}>
              <Collapsible.Body>
                <Table minWidth="760px" stickyScrollbar>
                  <Table.Body>
                    {extraData.map(renderRow)}
                  </Table.Body>
                </Table>
              </Collapsible.Body>
            </Collapsible>

            <Button
              appearance="transparent"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? (
                <>
                  <Icon source={<ChevronUpIcon />} color="currentColor" />
                  Ver menos
                </>
              ) : (
                <>
                  <Icon source={<ChevronDownIcon />} color="currentColor" />
                  Ver {sortedData.length - 10} productos mas
                </>
              )}
            </Button>
          </>
        )}

        <Text fontSize="caption" className="mt-4">
          Ranking basado en tasa de compra (compras/vistas). Solo productos con 10+ vistas.
        </Text>
      </Card.Body>
    </Card>
  )
}
